import React, { useEffect, useRef, useState, useCallback } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { MapPin, User, CheckCircle, XCircle, Clock, RefreshCw, AlertTriangle, Undo2, DollarSign, Pencil, Check, X } from "lucide-react";
import { cloudinaryThumb, cloudinaryPreview } from "../services/cloudinary";
import {
  getRoutesInRevision,
  approveRouteRevision,
  rejectRouteRevision,
  getAssignmentReadings,
  updateReading,
  toggleMeterExcluded,
  returnPendingMeters,
  updateAssignmentValor,
} from "../services/api";
import { useAuth } from "../context/AuthContext";

function ReadingsPreview({ assignmentId }) {
  const [readings, setReadings]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [editValues, setEditValues] = useState({}); // { route_meter_id: string }
  const [saving, setSaving]       = useState({}); // { route_meter_id: bool }
  const [excluding, setExcluding] = useState({}); // { route_meter_id: bool }
  const [lightbox, setLightbox]   = useState(null);
  const [zoom, setZoom]           = useState(null); // { url, x, y, ox, oy, level }
  const zoomLevel                 = zoom?.level ?? 1.5;
  const zoomActiveRef             = useRef(false);

  // Listener nativo no-pasivo para bloquear scroll de página mientras hay zoom activo
  useEffect(() => {
    function onWheel(e) {
      if (!zoomActiveRef.current) return;
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.25 : -0.25;
      setZoom((prev) => prev ? { ...prev, level: Math.min(4, Math.max(1, (prev.level ?? 1.5) + delta)) } : prev);
    }
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    setLoading(true);
    getAssignmentReadings(assignmentId)
      .then((data) => {
        setReadings(data);
        const vals = {};
        data.forEach((r) => { if (r.route_meter_id) vals[r.route_meter_id] = r.reading_value ?? ""; });
        setEditValues(vals);
      })
      .catch(() => setReadings([]))
      .finally(() => setLoading(false));
  }, [assignmentId]);

  async function saveValue(r) {
    const newVal = editValues[r.route_meter_id] ?? "";
    if (newVal === (r.reading_value ?? "")) return;
    setSaving((p) => ({ ...p, [r.route_meter_id]: true }));
    try {
      const updated = await updateReading(r.route_meter_id, {
        reading_value: newVal || null,
        original_value: r.admin_edited ? r.original_value : (r.reading_value ?? null),
      });
      setReadings((prev) => prev.map((x) =>
        x.route_meter_id === r.route_meter_id
          ? { ...x, reading_value: updated.reading_value, admin_edited: updated.admin_edited, original_value: updated.original_value }
          : x
      ));
    } catch (err) {
      window.alert(err.message || "Error al guardar");
    } finally {
      setSaving((p) => ({ ...p, [r.route_meter_id]: false }));
    }
  }

  async function toggleExclude(r) {
    if (!r.route_meter_id) return;
    setExcluding((p) => ({ ...p, [r.route_meter_id]: true }));
    try {
      const updated = await toggleMeterExcluded(r.route_id ?? assignmentId, r.route_meter_id);
      setReadings((prev) => prev.map((x) =>
        x.route_meter_id === r.route_meter_id ? { ...x, excluded: updated.excluded } : x
      ));
    } catch (err) {
      window.alert(err.message || "Error al actualizar");
    } finally {
      setExcluding((p) => ({ ...p, [r.route_meter_id]: false }));
    }
  }

  async function toggleNaturgy(r) {
    if (!r.route_meter_id) return;
    const newVal = !r.subido_a_naturgy;
    setSaving((p) => ({ ...p, [r.route_meter_id]: true }));
    try {
      await updateReading(r.route_meter_id, { subido_a_naturgy: newVal });
      setReadings((prev) => prev.map((x) =>
        x.route_meter_id === r.route_meter_id ? { ...x, subido_a_naturgy: newVal } : x
      ));
    } catch (err) {
      window.alert(err.message || "Error al guardar");
    } finally {
      setSaving((p) => ({ ...p, [r.route_meter_id]: false }));
    }
  }

  if (loading) return <div style={s.detailLoading}>Cargando mediciones...</div>;
  if (!readings || readings.length === 0)
    return <div style={s.detailEmpty}>Sin mediciones registradas</div>;

  const naturgyCount    = readings.filter((r) => r.subido_a_naturgy).length;
  const totalWithValue  = readings.filter((r) => r.reading_value).length;
  const incidentCount   = readings.filter((r) => r.has_incident).length;

  return (
    <>
      {/* Barra de progreso */}
      <div style={s.progressSummary}>
        <div style={s.progressStat}>
          <span style={s.progressNum}>{totalWithValue}</span>
          <span style={s.progressLbl}>/ {readings.length} con valor</span>
        </div>
        <div style={s.progressDivider} />
        {incidentCount > 0 && (
          <>
            <div style={s.progressStat}>
              <span style={{ ...s.progressNum, color: "#f59e0b" }}>{incidentCount}</span>
              <span style={s.progressLbl}>incidencias</span>
            </div>
            <div style={s.progressDivider} />
          </>
        )}
        <div style={s.progressStat}>
          <span style={{ ...s.progressNum, color: "#16a34a" }}>{naturgyCount}</span>
          <span style={s.progressLbl}>/ {readings.length} subidos Naturgy</span>
        </div>
      </div>

      {/* Grid de cards */}
      <div style={s.cardsGrid}>
        {readings.map((r, i) => {
          const isBusy = !!saving[r.route_meter_id];
          const isExcluding = !!excluding[r.route_meter_id];
          const hasValue = !!(editValues[r.route_meter_id] ?? r.reading_value);
          return (
            <div
              key={r.route_meter_id ?? r.reading_id ?? i}
              style={{
                ...s.meterCard,
                ...(r.excluded ? s.meterCardExcluded : {}),
                ...(r.has_incident && !r.excluded ? s.meterCardIncident : {}),
                ...(r.subido_a_naturgy ? s.meterCardDone : {}),
              }}
            >
              {/* Imagen grande */}
              <div
                style={s.imgWrap}
                onClick={() => r.image_url && setLightbox(r.image_url)}
                onMouseMove={(e) => {
                  if (!r.image_url) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const ox = (((e.clientX - rect.left) / rect.width) * 100).toFixed(1);
                  const oy = (((e.clientY - rect.top)  / rect.height) * 100).toFixed(1);
                  zoomActiveRef.current = true;
                  setZoom((prev) => ({ url: r.image_url, x: e.clientX, y: e.clientY, ox, oy, level: prev?.level ?? 1.5 }));
                }}
                onMouseLeave={() => { zoomActiveRef.current = false; setZoom(null); }}
              >
                {r.image_url ? (
                  <img
                    src={cloudinaryThumb(r.image_url, 200, 200)}
                    alt="medidor"
                    style={s.img}
                    loading="lazy"
                    onClick={() => setLightbox(cloudinaryPreview(r.image_url))}
                  />
                ) : (
                  <div style={s.imgEmpty}>
                    <span style={{ fontSize: 11, color: "var(--text-3)" }}>Sin foto</span>
                  </div>
                )}
                {/* Badges sobre la imagen */}
                {r.has_incident && (
                  <div style={s.badgeIncident}>
                    <AlertTriangle size={9} style={{ marginRight: 3 }} />Incidencia
                  </div>
                )}
                {r.subido_a_naturgy && (
                  <div style={s.badgeNaturgy}>✓ Naturgy</div>
                )}
              </div>

              {/* Info + controles */}
              <div style={s.cardBody}>
                {/* Código */}
                <div style={s.cardCode}>
                  {r.meter_code || <span style={{ color: "var(--text-3)", fontStyle: "italic" }}>Sin código</span>}
                </div>

                {/* Incidencia — detalle expandido */}
                {r.has_incident && (
                  <div style={s.incidentBox}>
                    <div style={s.incidentHeader}>
                      <AlertTriangle size={11} color="#92400e" style={{ flexShrink: 0 }} />
                      <span style={s.incidentCode}>{r.incident_code || "Incidencia"}</span>
                    </div>
                    {r.incident_description && (
                      <div style={s.incidentText}>{r.incident_description}</div>
                    )}
                    {r.incident_comment && (
                      <div style={s.incidentText}>{r.incident_comment}</div>
                    )}
                    {r.incident_image_url && (
                      <img
                        src={cloudinaryThumb(r.incident_image_url, 160, 120)}
                        alt="incidencia"
                        style={s.incidentImg}
                        loading="lazy"
                        onClick={() => setLightbox(cloudinaryPreview(r.incident_image_url))}
                      />
                    )}
                  </div>
                )}

                {/* Input valor */}
                <input
                  style={{
                    ...s.valueInput,
                    ...(hasValue ? s.valueInputFilled : {}),
                    ...(isBusy ? { opacity: 0.6 } : {}),
                  }}
                  value={editValues[r.route_meter_id] ?? r.reading_value ?? ""}
                  onChange={(e) =>
                    setEditValues((p) => ({ ...p, [r.route_meter_id]: e.target.value }))
                  }
                  onBlur={() => saveValue(r)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.target.blur();
                    if (e.key === "Tab") { e.preventDefault(); e.target.blur(); }
                  }}
                  placeholder="Valor de medición..."
                  disabled={!r.route_meter_id || isBusy}
                  autoComplete="off"
                />

                {/* Valor original si fue editado */}
                {r.admin_edited && r.original_value != null && (
                  <div style={s.origRow}>
                    <span style={s.origLabel}>✎ {r.admin_edited_by_name || "Admin"}</span>
                    <span style={s.origValue}>antes: {r.original_value}</span>
                  </div>
                )}

                {/* Checkbox Naturgy */}
                <label style={{
                  ...s.naturgyRow,
                  ...(r.subido_a_naturgy ? s.naturgyRowActive : {}),
                }}>
                  <input
                    type="checkbox"
                    checked={!!r.subido_a_naturgy}
                    onChange={() => toggleNaturgy(r)}
                    disabled={!r.route_meter_id || isBusy}
                    style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#16a34a", flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 700, color: r.subido_a_naturgy ? "#16a34a" : "var(--text-2)" }}>
                    Subido a Naturgy
                  </span>
                </label>

                {/* Botón excluir del total */}
                <button
                  style={{
                    ...s.excludeBtn,
                    ...(r.excluded ? s.excludeBtnActive : {}),
                    ...(isExcluding ? { opacity: 0.6 } : {}),
                  }}
                  onClick={() => toggleExclude(r)}
                  disabled={!r.route_meter_id || isExcluding}
                  title={r.excluded ? "Incluir en el total" : "Excluir del total"}
                >
                  {r.excluded ? "✓ Excluido del total" : "Excluir del total"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Lupa hover */}
      {zoom && (() => {
        const W = 320, H = 320;
        const margin = 16;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        let left = zoom.x + 20;
        let top  = zoom.y - H / 2;
        if (left + W > vw - margin) left = zoom.x - W - 20;
        if (top < margin)           top  = margin;
        if (top + H > vh - margin)  top  = vh - H - margin;
        return (
          <div style={{ ...s.zoomPopup, left, top, width: W, height: H, borderColor: zoomLevel >= 3 ? "#f59e0b" : "#D4620D" }}>
            <div style={s.zoomBadge}>{zoomLevel.toFixed(1)}×</div>
            <img
              src={zoom.url}
              alt="zoom"
              style={{
                ...s.zoomImg,
                transform: `scale(${zoomLevel})`,
                transformOrigin: `${zoom.ox}% ${zoom.oy}%`,
                transition: "transform 0.1s ease",
              }}
            />
          </div>
        );
      })()}

      {lightbox && (
        <div style={s.lightboxOverlay} onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="medidor" style={s.lightboxImg} />
          <button style={s.lightboxClose} onClick={() => setLightbox(null)}>✕</button>
        </div>
      )}
    </>
  );
}

export default function RevisionPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";

  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // routeId
  const [actionError, setActionError] = useState(null);
  const [returningPending, setReturningPending] = useState(null); // assignment id
  const [editingValor, setEditingValor] = useState(null); // assignment id
  const [valorInput, setValorInput] = useState("");
  const [savingValor, setSavingValor] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getRoutesInRevision()
      .then(setRoutes)
      .catch(() => setRoutes([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleApprove(routeId) {
    if (!window.confirm("¿Aprobar esta ruta? Quedará marcada como completada y se liberará el operario.")) return;
    setActionLoading(routeId);
    setActionError(null);
    try {
      await approveRouteRevision(routeId);
      setRoutes((prev) => prev.filter((r) => r.route_id !== routeId));
    } catch (err) {
      setActionError(err.message || "Error al aprobar");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReject(routeId, routeName) {
    if (!window.confirm(`¿Rechazar la ruta "${routeName}"? Volverá al estado activo para que el operario corrija.`)) return;
    setActionLoading(routeId);
    setActionError(null);
    try {
      await rejectRouteRevision(routeId);
      setRoutes((prev) => prev.filter((r) => r.route_id !== routeId));
    } catch (err) {
      setActionError(err.message || "Error al rechazar");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReturnPending(assignment) {
    if (!window.confirm(`¿Devolverle a ${assignment.employee_name} la ruta "${assignment.route_name}" solo con los medidores sin valor de lectura?`)) return;
    setReturningPending(assignment.id);
    try {
      const res = await returnPendingMeters(assignment.id);
      window.alert(`Se devolvió a ${assignment.employee_name} la ruta "${assignment.route_name}" con ${res.pending_count} medidor${res.pending_count !== 1 ? "es" : ""} sin valor de lectura.`);
    } catch (err) {
      window.alert(err.message || "Error al devolver la ruta");
    } finally {
      setReturningPending(null);
    }
  }

  function startEditValor(r) {
    setEditingValor(r.id);
    setValorInput(r.valor != null ? String(r.valor) : "");
  }

  function cancelEditValor() {
    setEditingValor(null);
    setValorInput("");
  }

  async function saveValor(assignment) {
    setSavingValor(true);
    try {
      const updated = await updateAssignmentValor(assignment.id, valorInput === "" ? null : parseFloat(valorInput));
      setRoutes((prev) => prev.map((r) => r.id === assignment.id ? { ...r, valor: updated.valor } : r));
      setEditingValor(null);
      setValorInput("");
    } catch (err) {
      window.alert(err.message || "Error al actualizar el valor");
    } finally {
      setSavingValor(false);
    }
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>
            Rutas en revisión
            {routes.length > 0 && (
              <span style={s.countBadge}>{routes.length}</span>
            )}
          </h1>
          <p style={s.subtitle}>
            Rutas enviadas por los operarios pendientes de aprobación
          </p>
        </div>
        <button style={s.refreshBtn} onClick={load} title="Actualizar">
          <RefreshCw size={15} />
        </button>
      </div>

      {actionError && (
        <div style={s.errorBanner}>{actionError}</div>
      )}

      {loading ? (
        <div style={s.loadingWrap}>Cargando...</div>
      ) : routes.length === 0 ? (
        <div style={s.emptyPage}>
          <Clock size={40} color="#d1d5db" style={{ marginBottom: 12 }} />
          <p style={{ color: "var(--text-2)", fontSize: 14, margin: 0 }}>
            No hay rutas pendientes de revisión
          </p>
        </div>
      ) : (
        <div style={s.list}>
          {routes.map((r) => {
            const isOpen = expanded === r.id;
            const busy = actionLoading === r.route_id;
            const pct =
              r.total_count > 0
                ? Math.round((r.checked_count / r.total_count) * 100)
                : 0;

            return (
              <div key={r.id} style={s.card}>
                {/* Card header */}
                <div style={s.cardHeader}>
                  <div style={s.cardLeft}>
                    <div style={s.routeDot} />
                    <div>
                      <div style={s.routeName}>{r.route_name}</div>
                      {r.zona && <div style={s.zonaBadge}>Zona {r.zona}</div>}
                    </div>
                  </div>

                  <div style={s.cardMeta}>
                    <div style={s.metaRow}>
                      <User size={12} color="var(--text-2)" />
                      <span style={s.metaText}>{r.employee_name}</span>
                    </div>
                    <div style={s.metaRow}>
                      <Clock size={12} color="var(--text-2)" />
                      <span style={s.metaText}>
                        {format(new Date(r.assigned_at), "d MMM yyyy", { locale: es })}
                      </span>
                    </div>
                    <div style={s.progressRow}>
                      <div style={s.progressBar}>
                        <div style={{ ...s.progressFill, width: `${pct}%` }} />
                      </div>
                      <span style={s.progressLabel}>
                        {r.checked_count}/{r.total_count} medidores
                      </span>
                    </div>
                    <div style={s.metaRow}>
                      <DollarSign size={12} color="var(--text-2)" />
                      {editingValor === r.id ? (
                        <>
                          <input
                            autoFocus
                            type="number"
                            min="0"
                            step="0.01"
                            style={s.valorInput}
                            value={valorInput}
                            onChange={(e) => setValorInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveValor(r);
                              if (e.key === "Escape") cancelEditValor();
                            }}
                            disabled={savingValor}
                          />
                          <button style={s.valorSaveBtn} onClick={() => saveValor(r)} disabled={savingValor} title="Guardar">
                            <Check size={11} />
                          </button>
                          <button style={s.valorCancelBtn} onClick={cancelEditValor} disabled={savingValor} title="Cancelar">
                            <X size={11} />
                          </button>
                        </>
                      ) : (
                        <>
                          <span style={s.metaText}>
                            {r.valor != null ? `$${Number(r.valor).toLocaleString("es-AR")}` : "Sin valor"}
                          </span>
                          {isAdminPlus && (
                            <button style={s.valorEditBtn} onClick={() => startEditValor(r)} title="Editar precio de la ruta">
                              <Pencil size={10} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={s.actions}>
                    {isAdminPlus && (
                      <button
                        style={{ ...s.btnApprove, ...(busy ? s.btnDisabled : {}) }}
                        onClick={() => handleApprove(r.route_id)}
                        disabled={busy}
                        title="Aprobar ruta"
                      >
                        <CheckCircle size={14} />
                        Aprobar
                      </button>
                    )}
                    {isAdminPlus && (
                      <button
                        style={{ ...s.btnReturnPending, ...(returningPending === r.id ? s.btnDisabled : {}) }}
                        onClick={() => handleReturnPending(r)}
                        disabled={returningPending === r.id}
                        title="Devolver al operario solo los medidores sin valor de lectura"
                      >
                        <Undo2 size={13} />
                        {returningPending === r.id ? "Devolviendo..." : "Devolver pendientes"}
                      </button>
                    )}
                    <button
                      style={{ ...s.btnReject, ...(busy ? s.btnDisabled : {}) }}
                      onClick={() => handleReject(r.route_id, r.route_name)}
                      disabled={busy}
                      title="Rechazar y devolver al operario"
                    >
                      <XCircle size={14} />
                      Rechazar
                    </button>
                    <button
                      style={s.btnExpand}
                      onClick={() => setExpanded(isOpen ? null : r.id)}
                    >
                      {isOpen ? "Ocultar" : "Ver mediciones"}
                    </button>
                  </div>
                </div>

                {r.notas && (
                  <div style={s.notas}>"{r.notas}"</div>
                )}

                {/* Expandable readings */}
                {isOpen && (
                  <div style={s.detailWrap}>
                    <ReadingsPreview assignmentId={r.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  page: { width: "100%" },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 28,
  },
  title: {
    fontSize: 24,
    fontWeight: 800,
    color: "var(--text)",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  countBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#D4620D",
    color: "#fff",
    borderRadius: 100,
    fontSize: 12,
    fontWeight: 700,
    padding: "2px 10px",
    minWidth: 24,
  },
  subtitle: {
    margin: "4px 0 0",
    fontSize: 13,
    color: "var(--text-2)",
  },
  refreshBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg-card)",
    color: "var(--text-2)",
    cursor: "pointer",
    flexShrink: 0,
  },
  errorBanner: {
    background: "var(--error-bg)",
    border: "1px solid var(--error-border)",
    color: "var(--error-text)",
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 13,
    marginBottom: 16,
  },
  loadingWrap: {
    padding: 40,
    textAlign: "center",
    color: "var(--text-2)",
    fontSize: 14,
  },
  emptyPage: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "60px 20px",
    color: "var(--text-2)",
  },
  list: { display: "flex", flexDirection: "column", gap: 12 },
  card: {
    background: "var(--bg-card)",
    borderRadius: 14,
    border: "1px solid var(--border)",
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    padding: "18px 20px",
    flexWrap: "wrap",
  },
  cardLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 180,
    flex: 1,
  },
  routeDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#f59e0b",
    flexShrink: 0,
  },
  routeName: {
    fontSize: 15,
    fontWeight: 700,
    color: "var(--text)",
  },
  zonaBadge: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    borderRadius: 100,
    padding: "2px 8px",
    marginTop: 3,
  },
  cardMeta: { flex: 1, display: "flex", flexDirection: "column", gap: 6 },
  metaRow: { display: "flex", alignItems: "center", gap: 6 },
  metaText: { fontSize: 12, color: "var(--text-2)" },
  valorInput: {
    width: 80,
    padding: "3px 6px",
    borderRadius: 6,
    border: "1px solid var(--border)",
    background: "var(--bg-card)",
    color: "var(--text-1)",
    fontSize: 12,
    fontFamily: "Montserrat, sans-serif",
  },
  valorEditBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    borderRadius: 6,
    border: "1px solid var(--border)",
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    cursor: "pointer",
    padding: 0,
  },
  valorSaveBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    borderRadius: 6,
    border: "1px solid rgba(22,163,74,0.3)",
    background: "rgba(22,163,74,0.1)",
    color: "#16a34a",
    cursor: "pointer",
    padding: 0,
  },
  valorCancelBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    borderRadius: 6,
    border: "1px solid var(--border)",
    background: "var(--bg-card)",
    color: "var(--text-2)",
    cursor: "pointer",
    padding: 0,
  },
  progressRow: { display: "flex", alignItems: "center", gap: 8, marginTop: 2 },
  progressBar: {
    flex: 1,
    height: 5,
    background: "var(--border)",
    borderRadius: 100,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "#16a34a",
    borderRadius: 100,
    transition: "width 0.3s",
  },
  progressLabel: { fontSize: 11, color: "var(--text-2)", whiteSpace: "nowrap" },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  btnApprove: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 8,
    border: "none",
    background: "#16a34a",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  btnReject: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "var(--bg-card)",
    color: "var(--error-text)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  btnReturnPending: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid rgba(212,98,13,0.3)",
    background: "rgba(212,98,13,0.06)",
    color: "#D4620D",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
    whiteSpace: "nowrap",
  },
  btnExpand: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  notas: {
    fontSize: 12,
    color: "var(--text-2)",
    fontStyle: "italic",
    padding: "0 20px 12px",
  },
  detailWrap: { borderTop: "1px solid var(--border)", padding: 20 },
  detailLoading: { color: "var(--text-2)", fontSize: 13, padding: 12 },
  detailEmpty:   { color: "var(--text-2)", fontSize: 13, padding: 12 },

  // Barra de progreso
  progressSummary: {
    display: "flex", alignItems: "center", gap: 16,
    background: "var(--bg-subtle)", borderRadius: 10,
    padding: "10px 16px", marginBottom: 16, border: "1px solid var(--border)",
  },
  progressStat:    { display: "flex", alignItems: "baseline", gap: 5 },
  progressNum:     { fontSize: 20, fontWeight: 800, color: "#D4620D" },
  progressLbl:     { fontSize: 12, color: "var(--text-2)", fontWeight: 600 },
  progressDivider: { width: 1, height: 28, background: "var(--border)" },

  // Cards grid
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: 12,
  },
  meterCard: {
    borderRadius: 12, border: "1px solid var(--border)",
    overflow: "hidden", background: "var(--bg-card)",
    display: "flex", flexDirection: "column",
    transition: "box-shadow .15s",
  },
  meterCardIncident: {
    border: "2px solid #f59e0b",
    boxShadow: "0 0 0 3px rgba(245,158,11,0.12)",
  },
  meterCardExcluded: {
    opacity: 0.5,
    border: "2px solid #dc2626",
    background: "#fef2f2",
  },
  excludeBtn: {
    width: "100%",
    padding: "5px 0",
    marginTop: 4,
    borderRadius: 7,
    border: "1px solid var(--border)",
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  excludeBtnActive: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#dc2626",
  },
  incidentBox: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 8,
    padding: "8px 10px",
    marginBottom: 6,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  incidentHeader: {
    display: "flex", alignItems: "center", gap: 5,
  },
  incidentCode: {
    fontSize: 11, fontWeight: 800, color: "#92400e",
    textTransform: "uppercase", letterSpacing: "0.04em",
  },
  incidentText: {
    fontSize: 11, color: "#78350f", lineHeight: 1.4,
  },
  incidentImg: {
    width: "100%", borderRadius: 6,
    objectFit: "cover", marginTop: 4,
    cursor: "pointer", maxHeight: 100,
  },
  meterCardDone: {
    border: "1.5px solid #86efac",
    boxShadow: "0 0 0 2px rgba(22,163,74,0.08)",
  },

  // Imagen
  imgWrap: {
    position: "relative", width: "100%", paddingBottom: "80%",
    background: "var(--bg-subtle)", cursor: "zoom-in", overflow: "hidden",
    flexShrink: 0,
  },
  img: {
    position: "absolute", inset: 0, width: "100%", height: "100%",
    objectFit: "cover",
  },
  imgEmpty: {
    position: "absolute", inset: 0, display: "flex",
    alignItems: "center", justifyContent: "center",
  },
  badgeIncident: {
    position: "absolute", bottom: 6, left: 6,
    display: "flex", alignItems: "center",
    background: "#f59e0b", color: "#fff",
    fontSize: 10, fontWeight: 700, borderRadius: 6, padding: "2px 7px",
  },
  badgeNaturgy: {
    position: "absolute", top: 6, right: 6,
    background: "#16a34a", color: "#fff",
    fontSize: 10, fontWeight: 700, borderRadius: 6, padding: "2px 7px",
  },

  // Body del card
  cardBody: { padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: 8, flex: 1 },
  cardCode: { fontSize: 13, fontWeight: 700, color: "var(--text)", lineHeight: 1.3 },

  valueInput: {
    width: "100%", padding: "8px 10px", borderRadius: 8,
    border: "1.5px solid var(--border)", fontSize: 14, fontWeight: 700,
    color: "var(--text)", background: "var(--bg)", outline: "none",
    fontFamily: "Montserrat, sans-serif", boxSizing: "border-box",
    transition: "border-color .15s",
  },
  valueInputFilled: {
    borderColor: "#D4620D", color: "#D4620D", background: "var(--primary-bg)",
  },

  origRow:   { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  origLabel: { fontSize: 10, fontWeight: 700, color: "#D4620D" },
  origValue: { fontSize: 10, color: "var(--text-3)", textDecoration: "line-through" },

  naturgyRow: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "7px 10px", borderRadius: 8,
    border: "1.5px solid var(--border)", cursor: "pointer",
    background: "var(--bg-subtle)", marginTop: "auto",
    transition: "all .15s",
  },
  naturgyRowActive: {
    borderColor: "#16a34a", background: "rgba(22,163,74,0.06)",
  },

  // Lupa hover
  zoomPopup: {
    position: "fixed", zIndex: 9998, pointerEvents: "none",
    borderRadius: 14, overflow: "hidden",
    boxShadow: "0 8px 40px rgba(0,0,0,0.45)",
    border: "3px solid #D4620D",
    background: "#000",
  },
  zoomImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  zoomBadge: {
    position: "absolute", top: 8, right: 8, zIndex: 1,
    background: "rgba(0,0,0,0.55)", color: "#fff",
    fontSize: 11, fontWeight: 700, borderRadius: 6, padding: "2px 8px",
    pointerEvents: "none",
  },
  zoomHint: {
    position: "absolute", bottom: 8, left: 0, right: 0,
    textAlign: "center", fontSize: 10, color: "rgba(255,255,255,0.7)",
    fontWeight: 600, pointerEvents: "none",
  },

  // Lightbox
  lightboxOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, cursor: "zoom-out" },
  lightboxImg:     { maxWidth: "92vw", maxHeight: "92vh", borderRadius: 10, objectFit: "contain" },
  lightboxClose:   { position: "absolute", top: 20, right: 24, background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 20, fontWeight: 700, cursor: "pointer", borderRadius: 10, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center" },
};
